import { CommonModule, Location } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  OnInit,
  OnDestroy,
  signal,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseService } from '../../services/course.service';
import { TranslateModule } from '@ngx-translate/core';
import { CtaButtonComponent } from '../../../../shared/components/ui/cta-button/cta-button.component';
import { ParentButtonComponent } from '../../../../shared/components/ui/parent-button/parent-button.component';
import { CourseHeaderComponent } from '../../components/course-header/course-header.component';
import { CourseVideoComponent } from '../../components/course-video/course-video.component';
import { CourseTagsComponent } from '../../components/course-tags/course-tags.component';
import { CourseInfoComponent } from '../../components/course-info/course-info.component';
import { CourseRatingComponent } from '../../components/course-rating/course-rating.component';
import { CourseMetaComponent } from '../../components/course-meta/course-meta.component';
import { CourseDescriptionComponent } from '../../components/course-description/course-description.component';
import { CourseStructureComponent } from '../../components/course-structure/course-structure.component';
import { QuizComponent } from '../../components/quiz/quiz.component';
import { CourseCertificateComponent } from '../../components/course-certificate/course-certificate.component';
import { challenge as ChallengeGen } from '@valvur/shared-grpc';

export type CourseDetailLayoutVariant = 'school' | 'parent';

@Component({
  standalone: true,
  selector: 'app-course-detail',
  imports: [
    CommonModule,
    TranslateModule,
    CtaButtonComponent,
    ParentButtonComponent,
    CourseInfoComponent,
    CourseMetaComponent,
    CourseTagsComponent,
    CourseVideoComponent,
    CourseHeaderComponent,
    CourseRatingComponent,
    CourseDescriptionComponent,
    CourseStructureComponent,
    QuizComponent,
    CourseCertificateComponent,
  ],
  templateUrl: './course-detail.component.html',
  styleUrl: './course-detail.component.scss',
  host: {
    '[class.course-detail-host--parent]': 'isParentLayout()',
  },
})
export class CourseDetailComponent implements OnInit, OnDestroy {
  #courseService = inject(CourseService);
  #location = inject(Location);
  #route = inject(ActivatedRoute);
  #router = inject(Router);

  layoutVariant = input<CourseDetailLayoutVariant>('school');

  readonly isParentLayout = computed(
    () =>
      this.layoutVariant() === 'parent' ||
      /\/home\/courses\/[^/?#]+/.test(this.#router.url),
  );

  courseId = signal<string | null>('');

  course = computed(() => {
    const id = this.courseId();
    return id ? this.#courseService.getStudyingCourseById(id) : undefined;
  });

  tasks = computed(() => this.course()?.tasks ?? []);

  /** Currently selected task (from course structure click) */
  selectedTask = signal<ChallengeGen.Task | null>(null);

  /** Whether the selected task's video has been fully watched */
  videoWatched = signal(false);

  /** Video URL: selected task's contentUrl, or empty when none selected */
  videoUrl = computed(() => {
    const task = this.selectedTask();
    return (task as { contentUrl?: string } | null)?.contentUrl ?? '';
  });

  /** Poster URL: course image when no task, else task thumbnail or course image */
  posterUrl = computed(() => {
    const task = this.selectedTask();
    const courseImg = this.course()?.courseImageUrl ?? '';
    if (task) {
      return (
        (task as { thumbnailUrl?: string }).thumbnailUrl ||
        (task as { coverUrl?: string }).coverUrl ||
        courseImg
      );
    }
    return courseImg;
  });

  /** Selected task has a quiz and video was watched → show quiz under video */
  selectedTaskWithQuiz = computed(() => {
    const task = this.selectedTask();
    if (!task || !this.videoWatched()) return null;
    const quizes = (task as { quizes?: { id: string }[] }).quizes;
    return quizes?.length && quizes[0]?.id ? task : null;
  });

  /** Selected task has a quiz and is completed → show "Quiz passed" */
  selectedTaskQuizPassed = computed(() => {
    const task = this.selectedTask();
    if (!task || !(task as { completed?: boolean }).completed) return false;
    const quizes = (task as { quizes?: { id: string }[] }).quizes;
    return !!(quizes?.length && quizes[0]?.id);
  });

  /** Last quiz result for richer feedback (correctCount, total, isPerfect) */
  lastQuizResult = signal<{
    correctCount: number;
    total: number;
    isPerfect: boolean;
  } | null>(null);

  /** Whether course was just completed (100% after refetch) */
  courseJustCompleted = signal(false);

  thumbnails = computed(() =>
    (this.tasks() ?? [])
      .map((t) => t.coverUrl || t.thumbnailUrl)
      .filter((url): url is string => !!url),
  );

  /** Show join when not joined AND not completed (can't join a passed course) */
  shouldShowJoinButton = computed(() => {
    const c = this.course() as {
      courseJoined?: boolean;
      courseCompleted?: boolean;
    } | undefined;
    return !!(this.courseId() && !c?.courseJoined && !c?.courseCompleted);
  });

  /** Show leave when joined AND not completed (can't leave a passed course) */
  shouldShowCancelButton = computed(() => {
    const c = this.course() as {
      courseJoined?: boolean;
      courseCompleted?: boolean;
    } | undefined;
    return !!(this.courseId() && c?.courseJoined && !c?.courseCompleted);
  });

  /** Show certificate when course is completed */
  shouldShowCertificate = computed(() => {
    const c = this.course() as { courseCompleted?: boolean } | undefined;
    return !!c?.courseCompleted;
  });

  /** Rating allowed only when joined AND completed */
  shouldAllowRating = computed(() => {
    const c = this.course() as {
      courseJoined?: boolean;
      courseCompleted?: boolean;
    } | undefined;
    return !!(c?.courseJoined && c?.courseCompleted);
  });

  headerCompletionPercent = computed(() => {
    if (!this.shouldShowCancelButton() && !this.shouldShowCertificate()) return undefined;
    const c = this.course() as { completionPercent?: number } | undefined;
    return c?.completionPercent ?? 0;
  });

  /** "X of Y lessons" for header when enrolled or completed */
  tasksProgress = computed(() => {
    if (!this.shouldShowCancelButton() && !this.shouldShowCertificate()) return undefined;
    const tasks = this.tasks();
    const completed = tasks.filter(
      (t) => (t as { completed?: boolean }).completed,
    ).length;
    const total = tasks.length;
    return total > 0 ? { completed, total } : undefined;
  });

  constructor() {
    effect(() => {
      if (this.shouldShowJoinButton()) {
        this.selectedTask.set(null);
      }
    });
  }

  ngOnInit(): void {
    this.courseId.set(this.#route.snapshot.paramMap.get('id') ?? '');
    this.#courseService.isCourseDetailPageEnabled.set(true);
  }

  ngOnDestroy(): void {
    this.#courseService.isCourseDetailPageEnabled.set(false);
  }

  isJoinPending = computed(() => this.#courseService.joinCourse.isPending());

  isLeavePending = computed(() => this.#courseService.leaveCourse.isPending());

  onJoinCourse(): void {
    const id = this.courseId();
    if (id) this.#courseService.joinCourse.mutate(id);
  }

  onLeaveCourse(): void {
    const id = this.courseId();
    if (id) this.#courseService.leaveCourse.mutate(id);
  }

  onRateCourse(rating: number): void {
    const courseId = this.courseId();
    if (courseId) {
      this.#courseService.rateCourse.mutate({ courseId, rating });
    }
  }

  goBack(): void {
    if (this.#router.url.includes('/home/courses/')) {
      this.#router.navigate(['/home/courses']);
    } else {
      this.#location.back();
    }
  }

  onTaskClick(task: ChallengeGen.Task): void {
    const current = this.selectedTask();
    if (current?.id === task.id) {
      this.selectedTask.set(null);
    } else {
      this.selectedTask.set(task);
      this.videoWatched.set(false);
      this.lastQuizResult.set(null);
    }
  }

  onVideoEnded(): void {
    this.videoWatched.set(true);
  }

  onQuizCompleted(result?: {
    correctCount: number;
    total: number;
    isPerfect: boolean;
  }): void {
    if (result) {
      this.lastQuizResult.set(result);
    }
    this.selectedTask.update((t) =>
      t ? ({ ...t, completed: true } as ChallengeGen.Task) : null,
    );
    const wasComplete =
      (this.course() as { courseCompleted?: boolean } | undefined)
        ?.courseCompleted === true;
    this.#courseService.refetchStudyingCourses().then(() => {
      const course = this.#courseService.getStudyingCourseById(
        this.courseId() ?? '',
      );
      const nowComplete =
        (course as { courseCompleted?: boolean } | undefined)
          ?.courseCompleted === true;
      if (!wasComplete && nowComplete) {
        this.courseJustCompleted.set(true);
        setTimeout(() => this.courseJustCompleted.set(false), 8000);
      }
    });
  }
}
